/**
 * Report (Case Report) entity model — ES module version of MyScripts/Entities/Report.ts
 */

import type { ReportData, AttachmentInfoData } from '@/services/api-types';

export type { ReportData };

// ── Report sub-entity interfaces (not in api-types.ts) ───────────────

export interface NpsdReportConnectionsData {
  clients: ReportClientGroupData[];
  businesses: ReportBusinessData[];
  people: ReportPersonData[];
  vehicles: ReportVehicleData[];
}

export interface ReportClientGroupData {
  id: number | null;
  name?: string;
  sites: number[];
  allSites: any[];
}

export interface ReportBusinessData {
  id?: number;
  businessName?: string;
  branchName?: string;
  website?: string;
  connections?: any;
}

export interface ReportPersonData {
  id?: number;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  dob?: string | null;
  gender?: number;
  race?: number;
  connections?: any;
  roles: number[];
}

export interface ReportVehicleData {
  id?: number;
  make?: string;
  model?: string;
  year?: string;
  color?: string;
  plateNumber?: string;
  plateState?: string;
  connections?: any;
}

// ── Date field helpers ───────────────────────────────────────────────

export const REPORT_DATE_FIELDS = [
  'callDateTime',
  'primaryDispatched',
  'primaryEnroute',
  'primaryOnScene',
  'primaryCleared',
  'incidentOccurredFrom',
  'incidentOccurredTo',
  'notifiedOfIncidentTime',
  'dob',
] as const;

export function isReportDateField(name: string): boolean {
  return (REPORT_DATE_FIELDS as readonly string[]).includes(name);
}

// ── Factory ──────────────────────────────────────────────────────────

export function createNpsdReportConnections(): NpsdReportConnectionsData {
  return { clients: [], businesses: [], people: [], vehicles: [] };
}

export function createReport(data?: Partial<ReportData>): ReportData {
  return {
    id: null,
    caseId: '',
    lockedFor: null,
    authorId: null,
    authorTimeZoneId: null,
    isSubmitted: null,
    isApproved: null,
    callCardId: null,
    originalPDF: null,
    isInternalInvestigator: null,
    reportSubmittedForApproval: null,
    returnToAuthorComments: '',
    reportApproved: null,
    reportSubmitted: null,
    natureOfCall: null,
    natureOfCallValues: null,
    incidentDescription: '',
    incidentOccurredFrom: null,
    incidentOccurredTo: null,
    notifiedOfIncidentVia: null,
    notifiedOfIncidentTime: null,
    notifiedPerson: null,
    notifiedAs: null,
    callLocName: '',
    callLocAddress: '',
    callLocAddress2: '',
    callLocCity: '',
    callLocState: '',
    callLocZip: '',
    callLocCountry: '',
    callLocFormattedAddress: '',
    latitude: null,
    longitude: null,
    customLocationId: null,
    callDisposition: null,
    callDispositionValue: null,
    dispositionNotes: null,
    primaryOfficer: null,
    primaryDispatched: null,
    primaryEnroute: null,
    primaryOnScene: null,
    primaryCleared: null,
    backupUnits: [],
    respondingAgencies: [],
    offenders: [],
    accident: { accidentPersonList: [], roadwayConditions: '', findings: '', notes: '' },
    propertyList: [],
    medicalReports: [],
    statementList: [],
    digitalEvidenceList: [],
    narrative: '',
    narrativeAttachmentInfoList: [],
    ...data,
  };
}
